import React, { useContext } from "react";
import { SelectButton } from "primereact/selectbutton";
import { SettingsContext } from "./SettingsContext.tsx";
import { ladderForSettings, levelName, ceilingOf, fitFactLevel } from "../FactLevels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { useLocale } from "../../Shared/i18n/LocaleContext.tsx";
import { format } from "../../Shared/i18n/format.ts";

/**
 * The level picker (ADR 0071 §5). It stands where the two value ranges stand
 * on an ordinary math-facts sheet, and replaces them while levels are on: the
 * level is how this sheet chooses its facts. The panel is basic and whole
 * (ADR 0023 §2); the toggle that brings it is advanced.
 *
 * A native <select>, like the format picker, for the same reasons: eleven to
 * thirteen levels as buttons would be taller than the rail, and the platform
 * picker is the better control on a phone.
 */
export function FactLevelSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();
    const locale = useLocale();
    const ladder = ladderForSettings(settings);

    if (!ladder) {
        // + with x, say: two ladders of different lengths, so no level means
        // anything. The sheet is an ordinary one; this says why.
        return <p className='settingNote factLevelCrossed'>{t('settings.factLevelCrossed')}</p>;
    }

    const multiplicative = ladder.id.startsWith("mul") || ladder.id.startsWith("div");
    const selected = Math.min(settings.FactLevel, ladder.levels.length);

    function handleLevel(value: string) {
        setSettings({ ...settings, FactLevel: Number(value) });
    }

    // Practice is the level's own facts with a signature line and the key off (ADR 0071 §8). The key
    // goes off with it in one update; the visitor can turn it back on. Choosing Test puts back
    // only the sheet type, since whether the key is on is the visitor's.
    function handleSheet(value: 'test' | 'practice') {
        // A null from SelectButton means the selected option was clicked again.
        if (value !== 'test' && value !== 'practice') {
            return;
        }
        setSettings(
            value === 'practice'
                ? { ...settings, FactLevelSheet: 'practice', AnswerPages: false }
                : { ...settings, FactLevelSheet: 'test' }
        );
    }

    function handleCeiling(value: 10 | 12) {
        // A null from SelectButton means the selected option was clicked again.
        if (value !== 10 && value !== 12) {
            return;
        }
        setSettings(fitFactLevel({ ...settings, FactLevelCeiling: value }));
    }

    return (
        <div className='factLevelSettings'>
            <div className='field'>
                <label htmlFor='factLevel'>{t('settings.factLevel')}</label>
                <select
                    id='factLevel'
                    className='input factLevelSelect'
                    value={selected}
                    onChange={(e) => handleLevel(e.target.value)}
                >
                    {ladder.levels.map((level) => (
                        <option key={level.id} value={level.id}>
                            {format(t('settings.factLevelOption'), {
                                n: level.id,
                                name: levelName(level, ladder.id, locale),
                                count: level.focus.length,
                            })}
                        </option>
                    ))}
                </select>
            </div>
            <div className='factLevelSheet'>
                <span className='settingLabel'>{t('settings.factLevelSheet')}</span>
                <SelectButton
                    value={settings.FactLevelSheet === 'practice' ? 'practice' : 'test'}
                    onChange={(e) => handleSheet(e.value)}
                    options={[
                        { label: t('settings.factLevelSheetTest'), value: 'test' },
                        { label: t('settings.factLevelSheetPractice'), value: 'practice' },
                    ]}
                    className='select factLevelSheetSelect'
                    aria-label={t('settings.factLevelSheet')}
                />
            </div>
            {multiplicative && (
                <div className='factLevelCeiling'>
                    <span className='settingLabel'>{t('settings.factLevelCeiling')}</span>
                    <SelectButton
                        value={ceilingOf(settings)}
                        onChange={(e) => handleCeiling(e.value)}
                        options={[
                            { label: t('settings.factLevelTo10'), value: 10 },
                            { label: t('settings.factLevelTo12'), value: 12 },
                        ]}
                        className='select factLevelCeilingSelect'
                        aria-label={t('settings.factLevelCeiling')}
                    />
                </div>
            )}
        </div>
    );
}
